import React from 'react';
import { DualTaskPerformance } from '../interfaces/types';
import { Layers, Zap, Clock, BarChart2, AlertCircle } from 'lucide-react';
import { analyzeDualTaskPerformance, formatTrend } from '../utils/analysisUtils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface DualTaskAssessmentProps {
  performances: DualTaskPerformance[];
}

const DualTaskAssessment: React.FC<DualTaskAssessmentProps> = ({ performances }) => {
  // Analyze dual task performance
  const { 
    averagePerformance, 
    performanceTrend, 
    attentionSwitchRate, 
    errorRateTrend,
    taskTypePerformance 
  } = analyzeDualTaskPerformance(performances);
  
  // Format timestamp
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Calculate performance level rating
  const getPerformanceLevel = (score: number): string => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Satisfactory';
    if (score >= 60) return 'Fair';
    if (score >= 50) return 'Needs Improvement';
    return 'Poor';
  };
  
  // Get color based on performance level
  const getPerformanceColor = (score: number): string => {
    if (score >= 85) return 'text-green-500';
    if (score >= 70) return 'text-blue-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };
  
  // Prepare task type performance chart data
  const taskPerformanceData = taskTypePerformance.map(item => ({
    taskType: item.taskType,
    performance: Math.round(item.performance)
  }));
  
  return (
    <div className="bg-white rounded-lg shadow-sm p-5">
      <div className="flex items-center mb-4">
        <Layers className="text-orange-500 mr-2" />
        <h3 className="text-lg font-semibold">Dual-Task Performance</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-1">Overall Performance</h4>
          <div className="flex items-end">
            <span className={`text-2xl font-semibold ${getPerformanceColor(averagePerformance)}`}>
              {averagePerformance.toFixed(1)}%
            </span>
            <span className={`ml-2 text-sm ${
              performanceTrend > 0 ? 'text-green-500' : 
              performanceTrend < 0 ? 'text-red-500' : 
              'text-gray-500'
            }`}>
              {formatTrend(performanceTrend)}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{getPerformanceLevel(averagePerformance)} dual-task ability</p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-1">Attention Switch Rate</h4>
          <div className="flex items-end">
            <span className="text-2xl font-semibold">{attentionSwitchRate.toFixed(1)}</span>
            <span className="ml-1 text-sm text-gray-500">switches/min</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {attentionSwitchRate >= 8 
              ? 'High attention switching frequency' 
              : attentionSwitchRate >= 5
              ? 'Moderate attention switching'
              : 'Low attention switching frequency'}
          </p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-1">Error Rate Trend</h4>
          <div className="flex items-end">
            <span className={`text-2xl font-semibold ${
              errorRateTrend > 0 ? 'text-green-500' : 
              errorRateTrend < 0 ? 'text-red-500' : 
              'text-gray-500'
            }`}>
              {formatTrend(errorRateTrend)}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {errorRateTrend > 5 
              ? 'Significant improvement in error rate' 
              : errorRateTrend > 0
              ? 'Slight improvement in error rate'
              : errorRateTrend === 0
              ? 'Stable error rate'
              : errorRateTrend > -5
              ? 'Slight increase in errors'
              : 'Significant increase in errors'}
          </p>
        </div>
      </div>
      
      {taskPerformanceData.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium mb-2">Performance by Primary Task</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={taskPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="taskType" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Bar dataKey="performance" fill="#8884d8" name="Performance %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      <h4 className="text-sm font-medium mb-2">Recent Dual-Task Assessments</h4>
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {performances.slice(0, 5).map((performance, index) => (
          <div key={index} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center mb-1">
                  <span className="text-xs text-gray-500">
                    {formatTime(performance.timestamp)}
                  </span>
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                    performance.overallPerformance >= 80 ? 'bg-green-100 text-green-800' : 
                    performance.overallPerformance >= 60 ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-red-100 text-red-800'
                  }`}>
                    {getPerformanceLevel(performance.overallPerformance)}
                  </span>
                </div>
                <div className="text-sm font-medium">{performance.primaryTask}</div>
                <div className="text-xs text-gray-600">
                  while {performance.secondaryTask}
                </div>
              </div>
              
              <div className="text-right">
                <div className="flex items-center text-xs text-gray-500 justify-end">
                  <Clock size={14} className="mr-1" />
                  <span>{Math.round(performance.completionTime)}s</span>
                </div>
                <div className="mt-1 text-xs">
                  <span className={`font-medium ${getPerformanceColor(performance.overallPerformance)}`}>
                    {performance.overallPerformance.toFixed(1)}% overall
                  </span>
                </div>
              </div>
            </div>
            
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="bg-gray-50 rounded p-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-500">Primary Task</span>
                  <span className={`text-xs font-medium ${getPerformanceColor(performance.primaryTaskPerformance)}`}>
                    {performance.primaryTaskPerformance.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div 
                    className={`h-1.5 rounded-full ${
                      performance.primaryTaskPerformance >= 80 ? 'bg-green-500' : 
                      performance.primaryTaskPerformance >= 60 ? 'bg-yellow-500' : 
                      'bg-red-500'
                    }`}
                    style={{ width: `${performance.primaryTaskPerformance}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded p-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-500">Secondary Task</span>
                  <span className={`text-xs font-medium ${getPerformanceColor(performance.secondaryTaskPerformance)}`}>
                    {performance.secondaryTaskPerformance.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div 
                    className={`h-1.5 rounded-full ${
                      performance.secondaryTaskPerformance >= 80 ? 'bg-green-500' : 
                      performance.secondaryTaskPerformance >= 60 ? 'bg-yellow-500' : 
                      'bg-red-500'
                    }`}
                    style={{ width: `${performance.secondaryTaskPerformance}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-600">
              <div>
                <span className="block text-gray-500">Attention Switches</span>
                <span>{performance.attentionSwitches}</span>
              </div>
              <div>
                <span className="block text-gray-500">Error Rate</span>
                <span className={performance.errorRate > 20 ? 'text-red-500' : 'text-gray-700'}>
                  {performance.errorRate.toFixed(1)}%
                </span>
              </div>
              <div>
                <span className="block text-gray-500">Completion Time</span>
                <span>{Math.round(performance.completionTime)}s</span>
              </div>
            </div>
            
            {(performance.errorRate > 25 || 
              (performance.primaryTaskPerformance < 60 && performance.secondaryTaskPerformance < 60)) && (
              <div className="mt-2 text-xs flex items-start text-red-600 bg-red-50 p-2 rounded">
                <AlertCircle size={14} className="mr-1 flex-shrink-0 mt-0.5" />
                <span>
                  {performance.errorRate > 25 && performance.primaryTaskPerformance < 60
                    ? 'High error rate and low performance suggest significant challenges with divided attention'
                    : performance.errorRate > 25
                    ? 'High error rate indicates difficulty managing dual-task demands'
                    : 'Low performance scores indicate challenges in dividing attention effectively'}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DualTaskAssessment; 